import React from 'react';
import { useQuery, getEvents, getLocationNotices } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { useNavigate } from 'react-router-dom';
import { REGULAR_EVENTS_LIST_FILTERS } from '../events/eventFilters';
import { LocationHubOverview } from '../hub-overview/LocationHubOverview';
import { resolveLocationSlug } from '../../client/lib/locationPaths';
import { ChevronRight, Megaphone, MapPin } from 'lucide-react';
import { OpeningHoursPanel } from './OpeningHoursPanel';
function noticeTag(notice: { category?: string | null; metadata?: Record<string, unknown> | null }): string {
  const priority = (notice.metadata as { priority?: string } | null)?.priority;
  if (priority === 'very_high' || priority === 'high') return 'Featured';
  return notice.category || 'Notice';
}

function tagColor(tag: string): string {
  if (tag === 'Featured' || tag === 'Campaign') return 'bg-indigo-100 text-indigo-700';
  if (tag === 'Events') return 'bg-violet-100 text-violet-700';
  if (tag === 'Community') return 'bg-emerald-100 text-emerald-700';
  if (tag === 'Information') return 'bg-sky-100 text-sky-700';
  return 'bg-blue-100 text-blue-700';
}

function AnnouncementRow({ notice }: { notice: { id: string; title: string; description?: string | null; createdAt: string | Date; category?: string | null; metadata?: Record<string, unknown> | null } }) {
  const tag = noticeTag(notice);

  return (
    <div className="flex flex-col gap-2 py-3 border-b border-gray-100 last:border-0 sm:grid sm:grid-cols-[6.25rem_1fr] sm:gap-x-4 sm:gap-y-0.5 sm:items-start">
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full w-fit sm:text-center sm:w-full ${tagColor(tag)}`}>
        {tag}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-snug">{notice.title}</p>
        {notice.description && (
          <p className="text-xs text-gray-500 leading-relaxed mt-0.5 line-clamp-3 sm:line-clamp-none">{notice.description}</p>
        )}
      </div>
    </div>
  );
}

export function TodayEventsSection({ events, orgSlug, navigate }: { events: any[]; orgSlug?: string; navigate: (path: string) => void }) {
  const formatTime = (dt: string | Date) => {
    const d = new Date(dt);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-900 text-base">Today&apos;s Events</h2>
        <button
          onClick={() => navigate(orgSlug ? `/${orgSlug}/events` : '/events')}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          View all <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      {events.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-8 text-center">
          <p className="text-sm text-gray-400">No events scheduled for today.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => (
            <div
              key={event.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 sm:gap-4 cursor-pointer hover:border-gray-200 hover:shadow-md transition-all"
              onClick={() => navigate(orgSlug ? `/${orgSlug}/events/${event.id}` : `/events/${event.id}`)}
            >
              <div className="flex-shrink-0 text-center min-w-[48px]">
                <p className="text-lg font-bold text-blue-600 leading-none">{formatTime(event.startTime)}</p>
                {event.endTime && (
                  <p className="text-xs text-gray-400 mt-0.5">{formatTime(event.endTime)}</p>
                )}
              </div>
              <div className="w-px h-10 bg-gray-100 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{event.title}</p>
                {event.venueName && (
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{event.venueName}</span>
                  </p>
                )}
              </div>
              {event.eventType && (
                <span className="flex-shrink-0 text-xs font-medium bg-blue-50 text-blue-600 px-2.5 py-1 rounded-full">
                  {event.eventType}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

interface InformationDeskContentProps {
  locationId: string;
  isAdmin: boolean;
  orgSlug?: string;
  orgName?: string;
  /** When false, today's events are rendered elsewhere (e.g. above this block on the hub page). */
  showTodayEvents?: boolean;
  /** When true, opening hours show here (used when not embedded in hub hero). */
  showOpeningHoursInline?: boolean;
}

export function InformationDeskContent({
  locationId,
  isAdmin,
  orgSlug,
  orgName,
  showTodayEvents = true,
  showOpeningHoursInline = false,
}: InformationDeskContentProps) {
  const navigate = useNavigate();

  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
  const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();

  const { data: rawEvents = [] } = useQuery(
    getEvents,
    {
      locationId,
      filters: { ...REGULAR_EVENTS_LIST_FILTERS, startDate: todayStart, endDate: todayEnd },
    },
    { enabled: !!locationId }
  );
  const todayEvents = rawEvents as any[];

  const { data: rawNotices = [], isLoading: noticesLoading } = useQuery(
    getLocationNotices,
    { locationIds: locationId ? [locationId] : [] },
    { enabled: !!locationId }
  );

  const allNotices = rawNotices as any[];
  const infoDeskFlagged = allNotices.filter(
    (n) => (n.metadata as { infoDesk?: boolean } | null)?.infoDesk === true
  );
  const deskNotices = (infoDeskFlagged.length > 0 ? infoDeskFlagged : allNotices)
    .sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
    .slice(0, 8);

  return (
    <div className="w-full px-3 sm:px-4 py-3 sm:py-4 space-y-4">
      {showOpeningHoursInline && locationId && (
        <OpeningHoursPanel locationId={locationId} isAdmin={isAdmin} variant="inline" />
      )}

      <div>
        {noticesLoading && !!locationId ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
          </div>
        ) : deskNotices.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">No notices posted yet.</p>
        ) : (
          deskNotices.map((notice) => (
            <AnnouncementRow key={notice.id} notice={notice} />
          ))
        )}
      </div>

      {showTodayEvents && (
        <TodayEventsSection events={todayEvents} orgSlug={orgSlug} navigate={navigate} />
      )}
    </div>
  );
}

export default function InformationDeskPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();

  const locationId = selectedLocation?.id ?? '';
  const slug = selectedLocation?.slug ?? resolveLocationSlug('', null, selectedLocation ?? null);

  const isAdmin = !!user && (
    user.platformRole === 'ADMINISTRATOR' ||
    user.platformRole === 'PLATFORM_OWNER' ||
    user.platformRole === 'MODERATOR'
  );

  const deskSection = locationId ? (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-4 sm:px-5 sm:py-5">
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Information Desk</h2>
      <InformationDeskContent
        locationId={locationId}
        isAdmin={isAdmin}
        orgSlug={slug}
        orgName={selectedLocation?.name}
        showTodayEvents={false}
      />
    </section>
  ) : null;

  if (slug) {
    return (
      <div className="w-full px-3 sm:px-6 py-4 sm:py-6 pb-20 lg:pb-6">
        <LocationHubOverview slug={slug} locationId={locationId}>
          {deskSection}
        </LocationHubOverview>
      </div>
    );
  }

  return (
    <InformationDeskContent
      locationId={locationId}
      isAdmin={isAdmin}
      showOpeningHoursInline={!!locationId}
    />
  );
}
